import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useBrands } from "@/modules/brand";
import { useAuth } from "@/modules/auth";
import Input from "@/components/ui/input";
import IconLogout from "@/components/icons/icon-logout";
import Button from "@/components/ui/button";

const toKebabCase = (str: string): string => {
	return str
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, "") // Remove special characters
		.replace(/\s+/g, "-") // Replace spaces with hyphens
		.replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
		.replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

const brandSchema = z.object({
	name: z.string().min(1, "Brand name is required"),
	slug: z
		.string()
		.min(3, "Slug must be at least 3 characters")
		.regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens")
		.regex(/^[^-]/, "Slug cannot start with a hyphen")
		.regex(/[^-]$/, "Slug cannot end with a hyphen")
		.regex(/^(?!.*-{2})/, "Slug cannot contain two or more consecutive hyphens"),
	website_url: z.string().min(1, "Website URL/Social Media URL is required").url("Please enter a valid URL"),
});

type BrandFormData = z.infer<typeof brandSchema>;

export default function CreateBrandPage() {
	const navigate = useNavigate();
	const { createBrand, checkBrandSlugAvailability } = useBrands();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
	const [isCheckingSlug, setIsCheckingSlug] = useState(false);
	const [slugError, setSlugError] = useState<string | null>(null);
	const [isSlugAvailable, setIsSlugAvailable] = useState(false);
	const { updateProfile, getUserInfo, user, logout } = useAuth();

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		clearErrors,
		formState: { errors },
	} = useForm<BrandFormData>({
		resolver: zodResolver(brandSchema),
		mode: "onChange",
		defaultValues: {
			name: "",
			slug: "",
			website_url: "",
		},
	});

	const name = watch("name");
	const slug = watch("slug");

	useEffect(() => {
		if (!isSlugManuallyEdited) {
			const newSlug = toKebabCase(name);
			setSlugError(null); // Clear custom slug error immediately when auto-filling slug
			clearErrors("slug"); // Clear react-hook-form validation errors for slug
			setValue("slug", newSlug);
		}
	}, [name, isSlugManuallyEdited, setValue, clearErrors]);

	useEffect(() => {
		const isValidSlug = slug && slug.length >= 3 && /^[a-z0-9-]+$/.test(slug) && /^[^-]/.test(slug) && /[^-]$/.test(slug) && !/.*-{2}/.test(slug);

		if (!slug || !isValidSlug) {
			setSlugError(null);
			setIsSlugAvailable(false);
			return;
		}

		setIsSlugAvailable(false);
		setSlugError(null); // Clear error immediately when slug changes
		const timeoutId = setTimeout(async () => {
			setIsCheckingSlug(true);
			try {
				const isAvailable = await checkBrandSlugAvailability(slug);
				if (isAvailable) {
					setIsSlugAvailable(true);
				} else {
					setSlugError("This slug is already taken");
				}
			} catch {
				setSlugError("Failed to check slug availability");
			} finally {
				setIsCheckingSlug(false);
			}
		}, 500);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [slug, checkBrandSlugAvailability]);

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/login", { replace: true });
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	const onSubmit = async (data: BrandFormData) => {
		setIsLoading(true);
		setError(null);

		try {
			const brand = await createBrand(data);
			await updateProfile({ current_workspace_id: brand?.id });

			// Fetch fresh user data to get updated workspaces_count for isBrandAvailable
			const updatedUser = await getUserInfo();

			navigate(`/${updatedUser?.current_workspace_id ?? brand?.id}/dashboard`);
		} catch (error) {
			setError(error instanceof Error ? error.message : "An error occurred");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="w-full sm:w-lg flex-col p-0 sm:p-8">
			<div className="flex items-center justify-between mb-6 sm:mb-8">
				<img className="w-12 sm:w-16" src="/images/sayaboutus-logomark-light.svg" alt="Say About Us" />
				<button type="button" onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-tertiary hover:text-primary cursor-pointer">
					<IconLogout />
					Logout
				</button>
			</div>
			<h1 className="text-lg sm:text-xl font-semibold text-primary -leading-[1%]">Create your brand</h1>
			<p className="text-sm mt-2 sm:mt-3 text-tertiary font-medium mb-4 sm:mb-6">Set up your brand to start collecting testimonials from your customers.</p>

			{(() => {
				const partner = user?.partner;
				const trialDays = partner?.trial_duration_days;
				const hasPartnerTrial = !!trialDays;

				return hasPartnerTrial ? (
					<div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
						<div className="flex items-start gap-3">
							<div className="flex-shrink-0 rounded-full bg-emerald-100 p-2">
								<svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
									/>
								</svg>
							</div>
							<div>
								<h3 className="text-sm font-semibold text-emerald-800">
									You've unlocked a {trialDays}-day free trial through {partner.name}!
								</h3>
								<p className="mt-1 text-sm text-emerald-700">Enjoy full access to our best plan with unlimited text and video testimonials. No credit card required.</p>
							</div>
						</div>
					</div>
				) : null;
			})()}

			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="space-y-4">
					<Input label="Brand Name" type="text" placeholder="e.g., My Company" disabled={isLoading} error={errors.name?.message} {...register("name")} required />
					<Input
						label="Your Public URL"
						type="text"
						placeholder="e.g., my-company"
						disabled={isLoading}
						error={errors.slug?.message}
						inputPrefix={<span className="text-tertiary text-sm">whatpeople.sayabout.us/</span>}
						message={
							slug && !errors.slug?.message
								? isCheckingSlug
									? { text: "Checking availability...", status: "loading" }
									: slugError
										? { text: slugError, status: "error" }
										: isSlugAvailable
											? { text: "This URL is available!", status: "success" }
											: { text: "Enter a unique identifier for your public page", status: "message" }
								: { text: "Enter a unique identifier for your public page", status: "message" }
						}
						{...register("slug", {
							onChange: () => {
								setIsSlugManuallyEdited(true);
							},
						})}
						required
					/>
					<Input
						label="Website URL/Social Media URL"
						type="url"
						placeholder="https://example.com or https://x.com/example"
						disabled={isLoading}
						error={errors.website_url?.message}
						{...register("website_url")}
						required
					/>
					{error && <p className="text-xs text-red-500">{error}</p>}
					<div className="pt-4">
						<Button type="submit" disabled={isLoading || isCheckingSlug || !!slugError} className="w-full">
							{isLoading ? "Creating..." : "Create Brand"}
						</Button>
					</div>
				</div>
			</form>
		</div>
	);
}
